import axios from "axios";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUserState, getAdminState } from "../../app/selector";
import { setFilterRange, setGenderFilter } from "../../features/admin/adminSlice"; 
import { Container, Row, Col, Card, Button, Table, Form, OverlayTrigger, Popover } from "react-bootstrap";
import { Chat, Eye, Trash, ShieldLock, ShieldSlash, Search, CalendarEvent, People, PersonCircle } from "react-bootstrap-icons";
import { timeAgo } from "../common/time/timeAgo";
import ConfirmationDialog from "../common/dialog/ConfirmationDialog";
import Notification from "../common/dialog/Notification";
import { BACKEND_URL } from "constants";

const Administration = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { user } = useSelector(getUserState);
  const { filterRange = "All Time", genderFilter = "ALL" } = useSelector(getAdminState) || {};

  const [users, setUsers] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [show, setShow] = useState(false);
  const [success, setSuccess] = useState(true);
  const [deleteUserID, setDeleteUserID] = useState("");
  const [message, setMessage] = useState("");
  const [currentUserID, setCurrentUserID] = useState(null);
  const [responseMessage, setResponseMessage] = useState({ msg: undefined, cals: undefined });
  const [showMessage, setShowMessage] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [messageTimestamp, setMessageTimestamp] = useState({ incoming_message: null, outgoing_message: null });

  useEffect(() => {
    if (showMessage) {
      const timer = setTimeout(() => {
        setShowMessage(false);
        setCurrentUserID(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showMessage]);

  // Fetch pre-filtered users from backend whenever Redux state changes (persisted across navigation)
  useEffect(() => {
    if (user && user.privilege > 0) {
      axios.post(`${BACKEND_URL}/get-users`, { filterRange, genderFilter })
        .then((res) => setUsers(res.data))
        .catch((err) => console.error("Error fetching users:", err));
    }
  }, [user, filterRange, genderFilter]);

  const togglePermission = (userId, currentPrivilege) => {
    const newPrivilege = currentPrivilege === 2 ? 0 : 2;
    axios.put(`${BACKEND_URL}/api/users/${userId}/permission`, { privilege: newPrivilege })
      .then(() => {
        setUsers((prev) => prev.map((u) => u.user_id === userId ? { ...u, privilege: newPrivilege } : u));
      })
      .catch((err) => console.error("Error updating permission:", err));
  };

  // Only search text filtering runs client-side; database handles date/gender filters
  const filteredUsers = users.filter((u) => {
    return u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleDeleteUserID = (userID) => {
    setDeleteUserID(userID);
    setShow(true);
  };

  const deleteUser = (userId) => {
    axios.delete(`${BACKEND_URL}/api/users/${userId}`)
      .then(() => {
        setSuccess(true);
        setShowNotification(true);
        setUsers((prev) => prev.filter((u) => u.user_id !== userId));
      })
      .catch((err) => {
        setSuccess(false);
        setShowNotification(true);
        console.error(err);
      });
  };

  const handleMessageButton = (user_id) => {
    setCurrentUserID(user_id);
    axios.post(`${BACKEND_URL}/get-message-time`, { receiver_user_id: user_id })
      .then((res) => setMessageTimestamp(res.data))
      .catch((err) => console.error(err));
  };

  const handleSendMessage = () => {
    const payload = { receiver_user_id: currentUserID, content: message };
    axios.post(`${BACKEND_URL}/send-message`, payload)
      .then(() => {
        setResponseMessage({ msg: "Message sent successfully", cals: "success" });
        setShowMessage(true);
        setMessage("");
      })
      .catch(() => {
        setResponseMessage({ msg: "Failed to send message", cals: "danger" });
        setShowMessage(true);
      });
  };

  const renderPopover = () => (
    <Popover id="popover-basic" style={{ minWidth: '320px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
      <Popover.Header as="h3" style={{ backgroundColor: '#fff', borderBottom: '1px solid #e2e8f0', fontWeight: '600' }}>
        {messageTimestamp.incoming_message === null && messageTimestamp.outgoing_message === null ? "Send Message" : "View Message"}
      </Popover.Header>
      <Popover.Body>
        {messageTimestamp.incoming_message === null && messageTimestamp.outgoing_message === null ? (
          <Form>
            <Form.Control type="text" placeholder="Write something..." value={message} onChange={(e) => setMessage(e.target.value)} as="textarea" rows={3} className="mb-2" />
            <div className="d-flex justify-content-end gap-2">
              <Button size="sm" variant="light" onClick={() => setCurrentUserID(null)}>Cancel</Button>
              <Button size="sm" variant="dark" onClick={handleSendMessage} disabled={!message.trim()}>Send</Button>
            </div>
          </Form>
        ) : (
          <div>
            <p className="small text-muted mb-3">
              {messageTimestamp.outgoing_message !== null
                ? `Last texted ${timeAgo(messageTimestamp.outgoing_message)}`
                : `Received text ${timeAgo(messageTimestamp.incoming_message)}`}
            </p>
            <div className="d-flex gap-2">
              <Button size="sm" variant="light" className="w-50" onClick={() => setCurrentUserID(null)}>Close</Button>
              <Button size="sm" variant="dark" className="w-50" onClick={() => navigate("/inbox/" + currentUserID)}><Chat /> Open</Button>
            </div>
          </div>
        )}
      </Popover.Body>
    </Popover>
  );

  const formatDate = (timestampMs) => {
    if (!timestampMs || typeof timestampMs !== 'number') return "Never";
    return new Date(timestampMs).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const getCurrentFormattedDate = () => {
    return new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  if (!user || user.privilege === 0) return null;

  const filteredStandardClients = filteredUsers.filter(u => u.privilege === 0);
  const standardMaleCount = filteredStandardClients.filter(u => u.gender?.toLowerCase() === 'male').length;
  const standardFemaleCount = filteredStandardClients.filter(u => u.gender?.toLowerCase() === 'female').length;

  return (
    <div className="content-block bg-light p-3 rounded">
      <ConfirmationDialog text="Permanently delete this user?" show={show} setShow={setShow} operation="Delete User" variant="danger" onConfirm={() => deleteUser(deleteUserID)} />
      <Notification text={success ? "Operation completed!" : "Something went wrong!"} variant={success ? "success" : "danger"} show={showNotification} setShow={setShowNotification} />

      <Container fluid>
        <div className="d-flex flex-column align-items-center mb-4">
          <h1 className="fw-bold tracking-tight text-dark m-0" style={{ fontSize: '1.75rem' }}>Administration Dashboard</h1>
          <span className="text-muted small mt-1 fw-medium">{getCurrentFormattedDate()}</span>
        </div>

        <Row className="mb-4 g-3 align-items-stretch">
          <Col xs={12} sm={4}>
            <Card className="metric-card-modern p-3 shadow-sm border-0 h-100 d-flex flex-column justify-content-center">
              <div className="text-muted small fw-medium text-uppercase">Filtered Results</div>
              <div className="fs-2 fw-bold text-dark mt-1 lh-1">{filteredUsers.length}</div>
            </Card>
          </Col>
          
          <Col xs={12} sm={4}>
            <Card className="metric-card-modern p-3 shadow-sm border-0 h-100 d-flex flex-column justify-content-center">
              <div className="text-muted small fw-medium text-uppercase">Management Team</div>
              <div className="fs-2 fw-bold text-primary mt-1 lh-1">{filteredUsers.filter(u => u.privilege > 0).length}</div>
            </Card>
          </Col>

          <Col xs={12} sm={4}>
            <Card className="metric-card-modern p-3 shadow-sm border-0 h-100 d-flex flex-column justify-content-between">
              <div className="text-muted small fw-medium text-uppercase mb-1">Standard Clients</div>
              <div className="d-flex justify-content-between align-items-end mt-1">
                <div>
                  <span className="fs-2 fw-bold text-success lh-1">{filteredStandardClients.length}</span>
                  <span className="ms-1 small fw-medium text-muted">All</span>
                </div>
                
                <div className="d-flex gap-3 text-end mb-1">
                  <div>
                    <div className="fw-bold text-primary fs-5 lh-1">{standardMaleCount}</div>
                    <div style={{fontSize: '0.65rem'}} className="text-muted text-uppercase fw-semibold mt-1">Male</div>
                  </div>
                  <div>
                    <div className="fw-bold fs-5 lh-1" style={{color: '#e11d48'}}>{standardFemaleCount}</div>
                    <div style={{fontSize: '0.65rem'}} className="text-muted text-uppercase fw-semibold mt-1">Female</div>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        <Card className="border-0 shadow-sm mb-4 p-3" style={{ borderRadius: '16px' }}>
          <Row className="g-3 align-items-center">
            <Col xs={12} md={6}>
              <div className="position-relative">
                <Search className="position-absolute text-muted" style={{ left: '12px', top: '12px' }} />
                <Form.Control type="text" placeholder="Search by name or email address..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px', borderRadius: '10px' }} />
              </div>
            </Col>
            
            <Col xs={12} md={6} className="d-flex justify-content-md-end align-items-center gap-2 flex-wrap">
              
              {/* Compact Native Select for Gender (Linked to Redux) */}
              <div className="d-flex align-items-center bg-white border shadow-sm px-2" style={{ borderRadius: '10px' }}>
                <People size={16} className="text-dark me-1" />
                <Form.Select 
                  className="border-0 shadow-none fw-medium text-dark bg-transparent py-2 px-1" 
                  value={genderFilter} 
                  onChange={(e) => dispatch(setGenderFilter(e.target.value))}
                  style={{ cursor: 'pointer', outline: 'none', width: '100px' }}
                >
                  <option value="ALL">All</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </Form.Select>
              </div>

              {/* Compact Native Select for Registration Range (Linked to Redux) */}
              <div className="d-flex align-items-center bg-white border shadow-sm px-2" style={{ borderRadius: '10px' }}>
                <CalendarEvent size={16} className="text-dark me-1" />
                <Form.Select 
                  className="border-0 shadow-none fw-medium text-dark bg-transparent py-2 px-1" 
                  value={filterRange} 
                  onChange={(e) => dispatch(setFilterRange(e.target.value))}
                  style={{ cursor: 'pointer', outline: 'none', width: '115px' }}
                >
                  <option value="All Time">All Time</option>
                  <option value="Last 24 hours">Last 24h</option>
                  <option value="Last 7 Days">Last 7d</option>
                  <option value="Last 30 Days">Last 30d</option>
                  <option value="Last 90 Days">Last 90d</option>
                  <option value="Last 180 Days">Last 180d</option>
                </Form.Select>
              </div>

            </Col>
          </Row>
        </Card>

        {/* Desktop View Table Interface */}
        <Card className="desktop-table-view border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Table className="modern-table m-0 align-middle" hover responsive style={{ minWidth: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '5%' }} />
              <col style={{ width: '30%' }} />
              <col style={{ width: '25%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '16%' }} />
            </colgroup>
            <thead>
              <tr>
                <th className="ps-4">#</th>
                <th>Identity Name</th>
                <th>Email Address</th>
                <th>Last Login</th>
                <th>Joined Date</th>
                <th className="text-end pe-4">System Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u, i) => {
                const canDelete = !((user?.privilege === 2 && u.privilege === 2) || u.privilege === 1);
                const canMessage = !(user?.privilege === 2 && u.privilege === 2);
                const canManageRoles = user?.privilege === 1;

                return (
                  <tr key={u.user_id}>
                    <td className="ps-4 text-muted">{i + 1}</td>
                    <td className="username-cell-modern text-truncate" title={u.username}>
                      <div className="d-flex align-items-center gap-2">
                        {u.thumbnail ? (
                          <img src={`data:image/jpeg;base64,${u.thumbnail}`} alt={u.username} className="admin-avatar-desktop" />
                        ) : (
                          <PersonCircle className="text-secondary flex-shrink-0" size={28} />
                        )}
                        <div className="d-flex flex-column align-items-start text-truncate">
                          <div className="d-flex align-items-center w-100">
                            <span className="text-truncate d-inline-block" style={{ maxWidth: '90px' }}>{u.username}</span>
                            <span className={`inline-role-badge ${u.privilege === 1 ? 'main-admin' : u.privilege === 2 ? 'admin' : 'user'}`}>
                              {u.privilege === 1 ? 'Owner' : u.privilege === 2 ? 'Admin' : 'User'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="email-cell-modern text-truncate" title={u.email}><span>{u.email}</span></td>
                    <td>{formatDate(u.last_login)}</td>
                    <td>{formatDate(u.created_at)}</td>
                    <td className="text-end pe-4">
                      <div className="d-inline-flex gap-1">
                        <Button variant="light" className="border border-secondary-subtle" size="sm" onClick={() => navigate("/profile/" + u.user_id)}><Eye /></Button>

                        {canDelete && (
                          <Button variant="light" className="text-danger border border-danger-subtle" size="sm" onClick={() => handleDeleteUserID(u.user_id)}><Trash /></Button>
                        )}

                        {canMessage && (
                          <OverlayTrigger trigger="click" rootClose placement="left" overlay={renderPopover()} show={currentUserID === u.user_id} onToggle={(nextShow) => !nextShow && setCurrentUserID(null)}>
                            <Button variant="light" className="text-success border border-success-subtle" size="sm" onClick={() => handleMessageButton(u.user_id)}><Chat /></Button>
                          </OverlayTrigger>
                        )}

                        {canManageRoles && (
                          <Button variant="light" className="text-dark border border-dark-subtle" size="sm" onClick={() => togglePermission(u.user_id, u.privilege)}>
                            {u.privilege === 2 ? <ShieldSlash /> : <ShieldLock />}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card>

        {/* Mobile Cards View Interface */}
        <div className="mobile-cards-view">
          <Row className="g-3">
            {filteredUsers.map((u) => {
              const canDelete = !((user?.privilege === 2 && u.privilege === 2) || u.privilege === 1);
              const canMessage = !(user?.privilege === 2 && u.privilege === 2);
              const canManageRoles = user?.privilege === 1;

              return (
                <Col xs={12} key={u.user_id}>
                  <Card className="border-0 shadow-sm p-3" style={{ borderRadius: '14px' }}>
                    <div className="w-100 d-flex justify-content-between align-items-center mb-2">
                      <div style={{ flex: 1, minWidth: 0 }} className="pe-2">
                        <div className="fw-bold text-dark fs-5 d-flex align-items-center flex-wrap gap-1" title={u.username}>
                          <span className="text-truncate d-inline-block" style={{ maxWidth: '100%' }}>
                            {u.username}
                          </span>
                          <span className={`inline-role-badge ${u.privilege === 1 ? 'main-admin' : u.privilege === 2 ? 'admin' : 'user'}`} style={{ fontSize: '0.65rem' }}>
                            {u.privilege === 1 ? 'Owner' : u.privilege === 2 ? 'Admin' : 'User'}
                          </span>
                        </div>
                        <div className="text-muted small mt-1 text-truncate">{u.email}</div>
                      </div>
                      
                      <div className="flex-shrink-0">
                        {u.thumbnail ? (
                          <img src={`data:image/jpeg;base64,${u.thumbnail}`} alt={u.username} className="admin-avatar-mobile" />
                        ) : (
                          <PersonCircle className="text-secondary" size={36} />
                        )}
                      </div>
                    </div>
                    <hr className="my-2 text-muted opacity-25" />
                    <Row className="mb-3 g-1 small">
                      <Col xs={6} className="text-muted">Last Active:</Col>
                      <Col xs={6} className="text-end fw-medium">{formatDate(u.last_login)}</Col>
                      <Col xs={6} className="text-muted">Registered:</Col>
                      <Col xs={6} className="text-end fw-medium">{formatDate(u.created_at)}</Col>
                    </Row>
                    <div className="d-flex gap-2">
                      <Button variant="light" className="w-25 border" onClick={() => navigate("/profile/" + u.user_id)}><Eye /></Button>

                      {canMessage && (
                        <OverlayTrigger trigger="click" rootClose placement="top" overlay={renderPopover()} show={currentUserID === u.user_id} onToggle={(nextShow) => !nextShow && setCurrentUserID(null)}>
                          <Button variant="outline-success" className="w-25" onClick={() => handleMessageButton(u.user_id)}><Chat /></Button>
                        </OverlayTrigger>
                      )}

                      {canManageRoles && (
                        <Button variant="dark" className="w-50" onClick={() => togglePermission(u.user_id, u.privilege)}>Manage Role</Button>
                      )}

                      {canDelete && (
                        <Button variant="danger" className="border-0 ms-auto" onClick={() => handleDeleteUserID(u.user_id)}><Trash /></Button>
                      )}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>

        {showMessage && (
          <div className={`alert alert-${responseMessage.cals} position-fixed bottom-0 end-0 m-4 shadow-lg`} role="alert" style={{ zIndex: 1050 }}>
            {responseMessage.msg}
          </div>
        )}
      </Container>
    </div>
  );
};

export default Administration;